import Button from '@/components/Button'
import { Stack } from '@/components/Layout'
import Text from '@/components/Text'
import { lobbyUrl } from '@/lib/lobbyUrl'
import { Feedback, FeedbackInput } from '@/shared/api-generated-types'
import { useMediaQuery } from '@react-hookz/web'
import { useTranslation } from 'react-i18next'
import { gql, useQuery } from 'urql'
import { rbp } from '../../constants/measured-scope'
import * as styles from './style'

const QUERY_FEEDBACK = gql`
  query feedback($feedbackInput: FeedbackInput!) {
    feedback(input: $feedbackInput) {
      id
    }
  }
`
const AskFeedback = ({ feedbackLink }: { feedbackLink: string }): JSX.Element => {
  const { t } = useTranslation('common')
  const isSmallDevice = useMediaQuery(`only screen and (max-width : ${rbp.sm})`, { initializeWithValue: false })
  const isLargeDevice = useMediaQuery(`only screen and (min-width : ${rbp.xxl})`, { initializeWithValue: false })

  return (
    <Stack x className={styles.askFeedbackContainer}>
      <Stack y>
        <Text size="md" weight="lg" responsiveSize={{ xxs: 'xs', lg: 'md' }}>
          {t('feedback.ask.title')}
        </Text>
        <Text size="sm" weight="sm" responsiveSize={{ xxs: 'xs', lg: 'sm' }}>
          {t('feedback.ask.subtitle')}
          {isSmallDevice && (
            <Button
              className={styles.feedbackLink}
              fontSize="xs"
              target="_blank"
              href={feedbackLink}
              theme="noBackground"
              component="link"
              label={t('feedback.ask.label')}
            />
          )}
        </Text>
      </Stack>
      {!isSmallDevice && (
        <Button
          size={isLargeDevice ? 'md' : 'xs'}
          target="_blank"
          href={feedbackLink}
          theme="primary"
          component="link"
          label={t('feedback.ask.label')}
        />
      )}
    </Stack>
  )
}

const FeedbackSuccess = (): JSX.Element => {
  const { t } = useTranslation('common')
  const isSmallDevice = useMediaQuery(`only screen and (max-width : ${rbp.sm})`, { initializeWithValue: false })
  const isLargeDevice = useMediaQuery(`only screen and (min-width : ${rbp.xxl})`, { initializeWithValue: false })
  return (
    <Stack x className={styles.feedbackSuccessContainer}>
      <Stack y>
        <Text size="md" weight="lg" responsiveSize={{ xxs: 'xs', lg: 'md' }}>
          {t('feedback.success.title')}
        </Text>
        <Text size="sm" weight="sm" responsiveSize={{ xxs: 'xs', lg: 'sm' }}>
          {t('feedback.success.subtitle')}
        </Text>
      </Stack>
      {!isSmallDevice && (
        <Button
          size={isLargeDevice ? 'md' : 'xs'}
          target="_blank"
          theme="green"
          component="button"
          label={t('feedback.success.label')}
        />
      )}
    </Stack>
  )
}

export interface Props {
  sessionId: string
}
const FeedbackWidget = ({ sessionId }: Props): JSX.Element | null => {
  const feedbackLink = lobbyUrl(`/session/${sessionId}/feedback?source=class_notes`)
  const QUERY_VARS: Partial<FeedbackInput> = {
    sessionId,
  }
  // check if the feedback is already submitted for the current session
  const [{ data, fetching, error }] = useQuery<{ feedback: Feedback }>({
    query: QUERY_FEEDBACK,
    variables: { feedbackInput: QUERY_VARS },
    requestPolicy: 'network-only',
  })
  if (fetching || error) return null
  return data?.feedback ? <FeedbackSuccess /> : <AskFeedback feedbackLink={feedbackLink} />
}
export default FeedbackWidget
